import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector, useIsElectron } from 'app/hooks'
import {
  useLazyCheckConferenceActiveQuery,
  useLazyRejectAllConferenceQuery,
  useLazyRespondConferenceCallQuery,
} from 'app/services/conference.service'
import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { BUTTONS, INFORMATION } from 'locales/locales'
import {
  CONFERENCE_BLANK_DIMENSIONS,
  CONFERENCE_TYPE_VIDEO,
  MESSAGE_DATE_FORMAT,
} from 'utils/constants'
import { conferenceActions, TConferenceAlert } from 'app/slices/conference.slice'

import Loading from 'components/spinner/Loading'
import CustomIcon from 'components/icon/CustomIcon'
import CustomText from 'components/text/CustomText'
import CustomButton from 'components/button/CustomButton'
import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import { FixMeLater } from 'types'
import { useRingtone } from '../../utils/hooks/useRingtone'

// interface IAlertContainerProps {
//   isVisible: boolean
//   isExpand: boolean
// }

interface IAlertProps {
  index: number
  isExpand: boolean
  length: number
}
const ConferenceAlert = () => {
  const isElectron = useIsElectron()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const conferenceDetails = useAppSelector((state) => state.conference)
  const [windowApi, setWindowApi] = useState<FixMeLater>(null)

  const [rejectAll] = useLazyRejectAllConferenceQuery()
  const [checkConference] = useLazyCheckConferenceActiveQuery()
  const [respondConferenceCall] = useLazyRespondConferenceCallQuery()

  const { play, stop } = useRingtone(60000)

  const autoRejectRunningRef = useRef(false)

  const rejectConferenceByMeetingId = useCallback(
    async (meetingId: string) => {
      try {
        await respondConferenceCall({ meetingId, isAccepted: false })
      } catch (err) {
        console.log(err)
      } finally {
        dispatch(conferenceActions.rejectConference({ meetingId }))
      }
    },
    [dispatch, respondConferenceCall],
  )

  const autoRejectVisibleConferences = useCallback(async () => {
    if (autoRejectRunningRef.current) return
    autoRejectRunningRef.current = true

    try {
      const visibleMeetings = [...conferenceDetails.data]

      for (const item of visibleMeetings) {
        await rejectConferenceByMeetingId(item.meetingId)
      }
    } finally {
      autoRejectRunningRef.current = false
    }
  }, [conferenceDetails.data, rejectConferenceByMeetingId])



  useEffect(() => {
    if (!conferenceDetails.visible || conferenceDetails.data.length === 0) {
      stop()
      return
    }
  
    play(() => {
      autoRejectVisibleConferences()
    })
  }, [
    conferenceDetails.visible,
    conferenceDetails.data.length,
    play,
    stop,
    autoRejectVisibleConferences,
  ])



  useEffect(() => {
    const w = window as FixMeLater
    if (w.api) setWindowApi(w.api)


    if (conferenceDetails.visible && windowApi) {
      conferenceDetails.data.forEach(
        (conference: { isActive: boolean; title: string; conferenceType: string; url: string }) => {
          const notifyType =
            conference.conferenceType === 'video' ? 'videoConference' : 'audioConference'
          windowApi.send('sendConferenceNotify', {
            type: notifyType,
            title:
              notifyType === 'videoConference' ? t('MESSAGE_VIDEO_CALL') : t('MESSAGE_AUDIO_CALL'),
            msg: conference.title,
            url: conference.url,
          })
        },
      )
    }
  }, [conferenceDetails.data, conferenceDetails.visible, windowApi, t])

  const onClickAlert = async (url: string, index: number, meetingId: string) => {
    stop()
    if (isElectron && windowApi) {

      const conference = conferenceDetails.data.find(
        (c: { meetingId: string }) => c.meetingId === meetingId,
      )
      if (conference) {
        await respondConferenceCall({ meetingId, isAccepted: true })
        dispatch(conferenceActions.rejectConference({ meetingId }))
        const notifyType =
          conference.conferenceType === CONFERENCE_TYPE_VIDEO
            ? 'videoConference'
            : 'audioConference'
        windowApi.send('sendVideoCallClick', {
          type: notifyType,
          title:
            notifyType === 'videoConference' ? t('MESSAGE_VIDEO_CALL') : t('MESSAGE_AUDIO_CALL'),
          msg: conference.title,
          url: conference.url,
        })
      }
      return
    }

    try {
      Loading.ref.show()
      const data = await checkConference({ meetingId })
      if (data.data?.isActive) {
        await respondConferenceCall({ meetingId, isAccepted: true })
        dispatch(conferenceActions.rejectConference({ meetingId }))
        window.open(url, '', CONFERENCE_BLANK_DIMENSIONS)
      }
    } finally {
      Loading.ref.hide()
    }
  }

  const onClickCloseIcon = async (index: number, meetingId: string) => {
    try {
      stop()
      await rejectConferenceByMeetingId(meetingId)
    } catch (err) {
      console.log(err)
    }
  }

  const onClickClearAll = async () => {
    try {
      Loading.ref.show()
      stop()
      await rejectAll({})
      dispatch(conferenceActions.clearAllConference())
    } finally {
      Loading.ref.hide()
    }
  }

  if (conferenceDetails.data.length < 1) {
    return null
  }

  const displayedConferences = conferenceDetails.data

  return (
    <Wrapper isVisible={conferenceDetails.visible}>
      <TopSide>
        <CustomButton
          size="small"
          typography="body7"
          type="danger"
          text={t(BUTTONS.CLEAR_ALL)}
          onClick={onClickClearAll}
        />
      </TopSide>
      <AlertsContainer>
        {displayedConferences.map((item: TConferenceAlert, index: number) => (
          <div key={item.meetingId}>
            <Alert>
              <AlertContent>
                <StyledIcon
                  iconPreset="thin"
                  onClick={() => onClickCloseIcon(index, item.meetingId)}
                  id="close-icon"
                  index={index}
                  source={icons.crossIconMid}
                />
                <AvatarContainer>
                  <Avatar
                    text={item.title}
                    onClick={() => onClickAlert(item.url, index, item.meetingId)}
                    source={item.avatar}
                    size={AvatarSize.medium}
                  />
                  <CustomText typography="body7" color={colors.gray60} textAlign={'center'}>
                    {moment(item.date).format(MESSAGE_DATE_FORMAT)}
                  </CustomText>
                </AvatarContainer>
                <MidSide onClick={() => onClickAlert(item.url, index, item.meetingId)}>
                  <ContentWrapper>
                    <ContentTop>
                      <StyledCustomText typography="header3">
                        {t(INFORMATION.MEETING_INVITATION, { userName: item.from })}
                      </StyledCustomText>
                    </ContentTop>
                    <ContentBottom>
                      <CustomText typography="body3">{item.title}</CustomText>
                    </ContentBottom>
                  </ContentWrapper>
                </MidSide>
              </AlertContent>
              <AlertButtons>
                <CustomButton
                  size="medium"
                  typography="body3"
                  type="primary"
                  icon={icons.callIconFilled}
                  iconPreset={'large'}
                  text={t(BUTTONS.ACCEPT)}
                  onClick={() => onClickAlert(item.url, index, item.meetingId)}
                />
                <CustomButton
                  size="medium"
                  typography="body3"
                  type="danger"
                  icon={icons.callIconFilledRed}
                  iconPreset={'large'}
                  text={t(BUTTONS.REJECT)}
                  onClick={() => onClickCloseIcon(index, item.meetingId)}
                />
              </AlertButtons>
            </Alert>
          </div>
        ))}
      </AlertsContainer>
    </Wrapper>
  )
}

export default ConferenceAlert

const ContentTop = styled.div`
  display: flex;
  align-items: center;
`

const ContentBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledCustomText = styled(CustomText)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: break-spaces;
`

const StyledIcon = styled(CustomIcon).attrs((props: IAlertProps) => props)`
  z-index: 3;
  position: absolute;
  display: none;
  top: -6px;
  right: -6px;
`

const TopSide = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  position: sticky;
  top: 0;
  padding: 10px;
  z-index: 5;
  border-bottom: 1px solid ${colors.gray20};
`

const AlertsContainer = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow-y: auto;
  max-height: 'calc(80vh - 10px)';
`

const Alert = styled.div`
  width: 380px;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0px 2px 5px ${colors.gray60};
  background-color: ${colors.white};
  position: relative;
  &:hover {
    #close-icon {
      display: block;
    }
  }
`

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
`

const AlertContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`

const AlertButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px
`

const Wrapper = styled.div<{ isVisible: boolean }>`
  position: absolute;
  right: 0;
  top: 70px;
  width: 420px;
  max-height: ${({ isVisible }) => (isVisible ? '80vh' : '80vh')};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '-150%')});
  transition: all 700ms ease;
  z-index: 4;
`

const MidSide = styled.div`
  width: 250px;
  max-height: 70px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const ContentWrapper = styled.div`
  display: flex;
  height: 70px;
  justify-content: space-between;
  flex-direction: column;
`
